import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { AnalyticsCupNav } from './components/AnalyticsCupNav';
import './AnalyticsCup.scss';
import submissionsCSV from '../../assets/analytics_cup/analytics-cup-26.csv';
import { findVideo, youtubeUrl } from '../../assets/analytics_cup/videoLookup';

const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuotes = !inQuotes; }
        else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
        else { current += ch; }
    }
    result.push(current.trim());
    return result;
};

const parseCSV = (text) =>
    text.trim().split('\n').slice(1).map(line => {
        const v = parseCSVLine(line);
        return {
            id: v[0], title: v[1], track: v[2], name: v[3],
            finalist: v[4] === 'TRUE',
            honourableMention: v[5] === 'TRUE',
            dataUsed: v[6], github: v[7],
        };
    });

const capitalizeName = (name = '') =>
    name.toLowerCase().split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ').replace(/_/g, ' ');

const getLastName = (name = '') => {
    const parts = name.trim().split(' ');
    return parts[parts.length - 1].toLowerCase();
};

export const AnalyticsCupSubmissions = () => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [trackFilter, setTrackFilter] = useState('all');

    useEffect(() => {
        fetch(submissionsCSV)
            .then(r => r.text())
            .then(text => { setSubmissions(parseCSV(text)); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const sorted = [...submissions].sort((a, b) =>
        getLastName(a.name).localeCompare(getLastName(b.name))
    );

    const filtered = trackFilter === 'all'
        ? sorted
        : sorted.filter(s => s.track === trackFilter);

    const researchCount = submissions.filter(s => s.track === 'Research Track').length;
    const analystCount  = submissions.filter(s => s.track === 'Analyst Track').length;

    return (
        <div className="analytics-cup">
            <Helmet>
                <title>Submissions — Analytics Cup | PySport</title>
                <meta name="description" content="Browse all Analytics Cup 1.0 submissions from the global sports analytics community." />
            </Helmet>
            <AnalyticsCupNav />

            <div className="ac-submissions-page">
                <div className="container">
                    <div className="ac-submissions-page__header">
                        <h1 className="ac-submissions-page__title">All Submissions</h1>
                        <p className="ac-submissions-page__sub">
                            Every project submitted across all editions of the Analytics Cup.
                        </p>
                    </div>

                    <div className="ac-main__filters">
                        <div className="ac-main__filter-group">
                            <span className="ac-main__filter-label">Track</span>
                            {[
                                { value: 'all',            label: `All (${submissions.length})` },
                                { value: 'Research Track', label: `Research (${researchCount})` },
                                { value: 'Analyst Track',  label: `Analyst (${analystCount})` },
                            ].map(({ value, label }) => (
                                <button
                                    key={value}
                                    className={`ac-main__filter-btn ${trackFilter === value ? 'is-active' : ''}`}
                                    onClick={() => setTrackFilter(value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <p className="ac-main__loading">Loading submissions…</p>
                    ) : (
                        <div className="ac-main__submissions-grid">
                            {filtered.map(s => {
                                const trackSlug = s.track === 'Research Track' ? 'research' : 'analyst';
                                return (
                                    <div className={`ac-submission-card ac-submission-card--${trackSlug}`} key={s.id}>
                                        <div className="ac-submission-card__badges">
                                            <span className="ac-submission-card__track">{s.track}</span>
                                            {s.finalist && (
                                                <span className="ac-submission-card__badge ac-submission-card__badge--finalist">
                                                    Finalist
                                                </span>
                                            )}
                                            {s.honourableMention && (
                                                <span className="ac-submission-card__badge ac-submission-card__badge--mention">
                                                    Honourable Mention
                                                </span>
                                            )}
                                        </div>
                                        <h4 className="ac-submission-card__name">{capitalizeName(s.name)}</h4>
                                        <p className="ac-submission-card__title">
                                            <a href={s.github} target="_blank" rel="noopener noreferrer">
                                                {s.title}
                                            </a>
                                        </p>
                                        {(() => { const v = findVideo(s.name); return v ? (
                                            <a href={youtubeUrl(v.youtubeId)} target="_blank" rel="noopener noreferrer" className="ac-submission-card__video-link">
                                                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg>
                                                Watch presentation
                                            </a>
                                        ) : null; })()}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
